import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApi } from '../api';
import { readCache, writeCache } from './localStorage';
import type { SubjectBase } from './types';

export const SUBJECT_QUERY_KEY = ['ref', 'subjects'] as const;

const CACHE_KEY = 'subjects';

/**
 * Synchronous, non-hook read of the cached subject catalog. For pure helpers
 * (e.g. glyph/token resolvers) that run inside render loops where a hook can't
 * be called. Returns `[]` when the cache is cold — callers must fall back
 * gracefully. Reactive consumers should prefer `useSubjects`.
 */
export function getCachedSubjects<T extends SubjectBase = SubjectBase & { icon?: string }>(): T[] {
  return readCache<T[]>(CACHE_KEY)?.data ?? [];
}

const STALE_TIME = 30 * 60 * 1000; // 30 min
const GC_TIME = 60 * 60 * 1000; // 1 hour

export interface UseSubjectsResult<T extends SubjectBase> {
  query: UseQueryResult<T[]>;
  subjects: T[];
  subjectNameMap: Map<string, string>;
}

export function useSubjects<T extends SubjectBase>(): UseSubjectsResult<T> {
  const { fetchApi } = useApi();

  const cached = useMemo(() => readCache<T[]>(CACHE_KEY), []);

  const query = useQuery({
    queryKey: [...SUBJECT_QUERY_KEY],
    queryFn: async () => {
      const subjects = await fetchApi<T[]>('/subjects');
      writeCache(CACHE_KEY, subjects);
      return subjects;
    },
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.updatedAt,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const subjects = query.data ?? [];

  const subjectNameMap = useMemo(
    () => new Map(subjects.map(s => [s.id, s.displayName])),
    [subjects],
  );

  return { query, subjects, subjectNameMap };
}
